/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {createContext, useContext} from 'react'
import PropTypes from 'prop-types'
import {ChakraProvider} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useQuery} from '@tanstack/react-query'

// Removes focus for non-keyboard interactions for the whole application
import 'focus-visible/dist/focus-visible'

import {getThemeForBrand} from '../../theme'
import {BrandProvider, resolveBrandFromUrl} from '../../contexts/brand-context'
import {NavigationProvider} from '../../contexts/navigation-context'
import {LocalFonts} from '../_app/partials/fonts'
import {SwiperBaseStyles} from '../_app/partials/SwiperBaseStyles'
import {AdyenGlobalStyles} from '../_app/partials/AdyenGlobalStyles'
import {ThirdPartyGlobalStyles} from '../_app/partials/ThirdPartyGlobalStyles'
import {MultiSiteProvider} from '@salesforce/retail-react-app/app/contexts'
import {
    resolveSiteFromUrl,
    resolveLocaleFromUrl
} from '@salesforce/retail-react-app/app/utils/site-utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {getSiteEnvVarValue} from '../../utils/site-config'
import {createUrlTemplate} from '@salesforce/retail-react-app/app/utils/url'
import createLogger from '@salesforce/pwa-kit-runtime/utils/logger-factory'

import {CommerceApiProvider} from '@salesforce/commerce-sdk-react'
import {ScriptProvider} from '../../contexts/script-context'
import {withReactQuery} from '@salesforce/pwa-kit-react-sdk/ssr/universal/components/with-react-query'
import {useCorrelationId} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {getAppOrigin} from '@salesforce/pwa-kit-react-sdk/utils/url'
// ReactQueryDevtools - only load in development
const ReactQueryDevtools =
    process.env.NODE_ENV === 'development'
        ? require('@tanstack/react-query-devtools').ReactQueryDevtools
        : () => null
import {DEFAULT_DNT_STATE} from '@salesforce/retail-react-app/app/constants'
import {PUBLIC_VARIABLES} from '../../constants'

/**
 * Use the AppConfig component to inject extra arguments into the getProps
 * methods for all Route Components in the app – typically you'd want to do this
 * to inject a connector instance that can be used in all Pages.
 *
 * You can also use the AppConfig to configure a state-management library such
 * as Redux, or Mobx, if you like.
 */
const AppConfig = ({children, locals = {}}) => {
    const {correlationId} = useCorrelationId()
    const headers = {
        'correlation-id': correlationId
    }

    const commerceApiConfig = locals.appConfig.commerceAPI
    const appOrigin = getAppOrigin()
    const passwordlessCallback = locals.appConfig.login?.passwordless?.callbackURI
    const refreshTokenTTL = getSiteEnvVarValue(locals.appConfig, 'REFRESH_TOKEN_TTL', locals.site?.id) || 0

    // Resolve brand and select the corresponding Chakra theme
    const brandId = locals.brandId || 'garage'
    const theme = getThemeForBrand(brandId)

    return (
        <CommerceApiProvider
            shortCode={commerceApiConfig.parameters.shortCode}
            clientId={commerceApiConfig.parameters.clientId}
            organizationId={commerceApiConfig.parameters.organizationId}
            siteId={locals.site?.id}
            locale={locals.locale?.id}
            currency={locals.locale?.preferredCurrency}
            redirectURI={`${appOrigin}/callback`}
            proxy={`${appOrigin}${commerceApiConfig.proxyPath}`}
            headers={headers}
            defaultDnt={DEFAULT_DNT_STATE}
            logger={createLogger({packageName: 'commerce-sdk-react'})}
            passwordlessLoginCallbackURI={passwordlessCallback}
            // Set 'enablePWAKitPrivateClient' to true use SLAS private client login flows.
            // Make sure to also enable useSLASPrivateClient in ssr.js when enabling this setting.
            enablePWAKitPrivateClient={true}
            refreshTokenRegisteredCookieTTL={Number(refreshTokenTTL)}
        >
            <MultiSiteProvider site={locals.site} locale={locals.locale} buildUrl={locals.buildUrl}>
                <BrandProvider brandId={brandId}>
                    <NavigationProvider>
                        <ChakraProvider theme={theme}>
                            <LocalFonts />
                            <SwiperBaseStyles />
                            <AdyenGlobalStyles />
                            <ThirdPartyGlobalStyles />
                            <ScriptProvider>{children}</ScriptProvider>
                        </ChakraProvider>
                    </NavigationProvider>
                </BrandProvider>
            </MultiSiteProvider>
            <ReactQueryDevtools />
        </CommerceApiProvider>
    )
}

AppConfig.restore = (locals = {}) => {
    const path =
        typeof window === 'undefined'
            ? locals.originalUrl
            : `${window.location.pathname}${window.location.search}`
    const site = resolveSiteFromUrl(path)
    const locale = resolveLocaleFromUrl(path)

    const {app: appConfig} = getConfig()
    const apiConfig = {
        ...appConfig.commerceAPI,
        einsteinConfig: appConfig.einsteinAPI
    }

    apiConfig.parameters.siteId = site.id

    // Resolve brand from hostname (e.g. garage.localhost -> 'garage', dynamite.co.uk -> 'dynamite')
    const hostname =
        typeof window === 'undefined' ? locals.hostname || '' : window.location.hostname
    locals.brandId = resolveBrandFromUrl(hostname)

    locals.buildUrl = createUrlTemplate(appConfig, site.alias || site.id, locale.id)
    locals.site = site
    locals.locale = locale
    locals.appConfig = appConfig
}

AppConfig.freeze = () => undefined

AppConfig.extraGetPropsArgs = (locals = {}) => {
    return {
        buildUrl: locals.buildUrl,
        site: locals.site,
        locale: locals.locale
    }
}

AppConfig.propTypes = {
    children: PropTypes.node,
    locals: PropTypes.object
}

const isServerSide = typeof window === 'undefined'

// Recommended settings for PWA-Kit usages.
// NOTE: they will be applied on both server and client side.
// retry is always disabled on server side regardless of the value from the options
const options = {
    queryClientConfig: {
        defaultOptions: {
            queries: {
                retry: false,
                refetchOnWindowFocus: false,
                staleTime: 10 * 1000,
                ...(isServerSide ? {retryOnMount: false} : {})
            },
            mutations: {
                retry: false
            }
        }
    }
}

export default withReactQuery(AppConfig, options)
